import { boothClient } from '@terra/shared-rest-clients-booth';
import { toasterApi } from '@terra/ui-design-system';
import {
  tenantAwareApiWrapper,
  withOrchestratorClient,
} from '../../clients/tester-client/api-core';
import { useProjectStore } from '../../store/projectStore';
import { readBoothVaultPublicOriginFromSelectedApplication } from './booth-vault-public-origin';

type BoothHandoffSlot = {
  promise: Promise<void> | null;
};

const boothHandoffSlot: BoothHandoffSlot = ((
  globalThis as typeof globalThis & {
    __terraBoothHandoffSlot?: BoothHandoffSlot;
  }
).__terraBoothHandoffSlot ??= { promise: null });

const isDuplicateBoothResetError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  const normalized = error.message.toLowerCase();
  return (
    normalized.includes('booth reset already in progress') ||
    normalized.includes('api call failed with status: 409')
  );
};

export async function performBoothStationHandoff({
  resetPortalForNewRun,
  onRestartTour,
}: {
  resetPortalForNewRun: () => Promise<void>;
  onRestartTour: () => void;
}): Promise<void> {
  if (boothHandoffSlot.promise) {
    return boothHandoffSlot.promise;
  }

  boothHandoffSlot.promise = new Promise<void>((resolve, reject) => {
    void runBoothStationHandoff({
      resetPortalForNewRun,
      onRestartTour,
    }).then(resolve, reject);
  }).finally(() => {
    boothHandoffSlot.promise = null;
  });

  return boothHandoffSlot.promise;
}

async function runBoothStationHandoff({
  resetPortalForNewRun,
  onRestartTour,
}: {
  resetPortalForNewRun: () => Promise<void>;
  onRestartTour: () => void;
}): Promise<void> {
  const { targetId } = useProjectStore.getState();

  if (!targetId) {
    toasterApi.error({
      title: 'Booth handoff unavailable',
      description: 'Select an application in the app picker first.',
    });
    return;
  }

  const vaultPublicOrigin = readBoothVaultPublicOriginFromSelectedApplication();
  if (!vaultPublicOrigin) {
    toasterApi.error({
      title: 'Booth handoff unavailable',
      description:
        'Set the application URL (domain) on the selected app before reset.',
    });
    return;
  }

  try {
    await tenantAwareApiWrapper({
      useBodyMessage: true,
      endpointFn: async (headers) =>
        boothClient.resetApplication({
          params: { applicationId: targetId },
          body: { vaultPublicOrigin },
          ...withOrchestratorClient(headers),
        }),
    });

    try {
      await resetPortalForNewRun();
      onRestartTour();
    } catch (portalError: unknown) {
      throw new Error(
        portalError instanceof Error
          ? `Station reset on server succeeded, but Torch cleanup failed: ${portalError.message}`
          : 'Station reset on server succeeded, but Torch cleanup failed.',
      );
    }

    toasterApi.success({
      title: 'Station ready',
      description: 'Application reset for the next player. Tour restarted.',
    });
  } catch (error: unknown) {
    if (isDuplicateBoothResetError(error)) {
      return;
    }

    toasterApi.error({
      title: 'Booth reset failed',
      description:
        error instanceof Error
          ? error.message
          : 'Could not reset the booth application.',
    });
  }
}
